/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, type ReactNode, useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';

import useControlled from '@atlaskit/ds-lib/use-controlled';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { UniversalCreateBFFProvider } from '@atlassian/navigation-bff-data-provider/universal-create';
import { NavigationUFOSegment } from '@atlassian/navigation-perf-utils';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { MenuStateContext } from '../../../context';
import { GenericErrorBoundary } from '../../../services';
import type {
	MenuStateContextProps,
	MixedMenuItems,
	UniversalCreateButtonCombinedProps,
	UniversalCreateButtonProps,
	UniversalCreateButtonPropsV2,
} from '../../../types';
import { useNormalizeItems } from '../../../utils';
import AnalyticsWrapper from '../../components/AnalyticsWrapper';
import { GicModalProvider } from '../../components/CrossAppItem/primitives/GicModalContext';
import { EntryPointPopup } from '../../components/EntryPointPopup';
import { LoadingMenu } from '../../components/LoadingMenu';
import { TriggerButton } from '../../components/TriggerButton';
import { useIsResponsiveView } from '../../utils';

import { popupContentEntryPoint } from './entrypoint';
import { useJsChunkLoadTrack } from './hooks/use-js-chunk-load-track';

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props && props.buttonOnly !== true;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props && props.buttonOnly !== true;
}

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		isOpen,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
		buttonOnly = false,
	} = props;
	const isButtonOnly = buttonOnly && fg('navx_4208_uc_button_only_enabled');
	const primaryActionItem = 'primaryActionItem' in props ? props.primaryActionItem : undefined;
	const isResponsive = useIsResponsiveView();

	// Track JS chunk load time - must be called before the lazy chunk loads
	useJsChunkLoadTrack();

	// Normalize items for v1, or use empty array for v2
	const items = isV1Props(props) ? props.items : undefined;
	const structuredItems = useNormalizeItems(items ?? []);
	const isOpenStateRefactor = fg('navx-4182-uc-open-state-refactor');

	// Use useControlled hook to handle both controlled and uncontrolled state
	const [isLocalOpen, setLocalIsOpen] = useControlled(isOpen, () => false);

	const handleTriggerButtonClick = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(!isLocalOpen);
		}
		// Notify parent
		onClick?.();
	}, [isOpenStateRefactor, isLocalOpen, setLocalIsOpen, onClick]);

	const handleClose = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(false);
		}

		onClose?.();
	}, [isOpenStateRefactor, setLocalIsOpen, onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const menuStateContextValue = useMemo((): MenuStateContextProps => {
		const isV2Mode = isV2Props(props);
		const sections = isV2Mode && 'sections' in props && props.sections ? props.sections : [];
		const crossAppCreationConfig =
			isV2Mode && 'crossAppCreationConfig' in props
				? (props.crossAppCreationConfig ?? ({ enabled: false } as const))
				: ({ enabled: false } as const);
		const selectedItem = 'selectedItem' in props ? props.selectedItem : undefined;
		const controlledOpen = 'isOpen' in props ? props.isOpen : undefined;

		return {
			isV2Mode,
			items: fg('navx_uc_context_refactor')
				? structuredItems
				: isV1Props(props)
					? // keep previous code path, would be removed after fg rollout
						((props.items ?? []) as MixedMenuItems)
					: ([] as MixedMenuItems),
			product: props.product ?? 'unknown',
			subProduct: props.subProduct,
			isNav4Enabled,
			sections,
			crossAppCreationConfig,
			suggestionConfig:
				'suggestionConfig' in props
					? (props.suggestionConfig ?? { enabled: false })
					: { enabled: false },
			selectedItem,
			preventClose: props.preventClose,
			primaryActionItem,
			...(isOpenStateRefactor ? {} : { controlledOpen }),
		};
	}, [props, isNav4Enabled, structuredItems, primaryActionItem, isOpenStateRefactor]);

	const sharedTriggerButtonProps = useMemo(
		() => ({
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			onMouseEnter: handleMouseEnter,
			onMouseLeave,
			onClick: handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		}),
		[
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			handleMouseEnter,
			onMouseLeave,
			handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		],
	);

	return (
		<MenuStateContext.Provider value={menuStateContextValue}>
			<GicModalProviderWithFG>
				{isButtonOnly ? (
					<TriggerButton {...sharedTriggerButtonProps} buttonOnly>
						{children}
					</TriggerButton>
				) : (
					<EntryPointPopup
						{...(isOpenStateRefactor ? { isOpen: isLocalOpen } : {})}
						placement={
							(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
						}
						// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
						shouldRenderToParent={shouldRenderToParent}
						onClose={handleClose}
						trigger={(triggerProps) => (
							<TriggerButton
								{...sharedTriggerButtonProps}
								buttonOnly={undefined} // explicitly disable buttonOnly
								isEntryPointEnabled
								triggerProps={triggerProps}
								isOpen={triggerProps['aria-expanded']}
								primaryActionItem={primaryActionItem}
							>
								{children}
							</TriggerButton>
						)}
						entryPoint={popupContentEntryPoint}
						entryPointProps={
							props as Exclude<UniversalCreateButtonCombinedProps, { buttonOnly: true }>
						}
						fallback={<LoadingMenu padded />}
					/>
				)}
			</GicModalProviderWithFG>
		</MenuStateContext.Provider>
	);
}

function UFOWrapper({ children }: { children: ReactNode }) {
	return <NavigationUFOSegment id="universalCreateComponentLoad">{children}</NavigationUFOSegment>;
}

function NoopComponent<T>(props: { children: ReactNode; value?: T | undefined }) {
	return <Fragment>{props.children}</Fragment>;
}

const GicModalProviderWithFG = componentWithFG('navx_enable_gic', GicModalProvider, NoopComponent);

function UniversalCreateButtonLazyInner(props: UniversalCreateButtonCombinedProps) {
	return (
		<UFOWrapper>
			<ConditionalRelayEnvironmentProvider>
				<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
					<UniversalCreateBFFProvider>
						<AnalyticsWrapper>
							<FlagsProvider>
								<UniversalCreateButtonInner {...props} />
							</FlagsProvider>
						</AnalyticsWrapper>
					</UniversalCreateBFFProvider>
				</BFFProvider>
			</ConditionalRelayEnvironmentProvider>
		</UFOWrapper>
	);
}

export function UniversalCreateButtonLazy(props: UniversalCreateButtonCombinedProps) {
	return (
		<GenericErrorBoundary name="universal-create-button-lazy" app={props.product}>
			<UniversalCreateButtonLazyInner {...props} />
		</GenericErrorBoundary>
	);
}
